import { useForm, SubmitHandler } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { FC, useEffect } from "react";

import {
	useGetSeoDataMutation,
	useUpdateSeoDataMutation,
} from "../../../api/productsAPI";
import { TProductVariants, TProductSeoData } from "../../../api/api.types";

type IEditSeoProps = {
	show: boolean;
	onHide: () => void;
	product: TProductVariants;
};

const EditSeo: FC<IEditSeoProps> = ({ show, onHide, product }) => {
	const intl = useIntl();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<TProductSeoData>({
		defaultValues: {
			title: "",
			description: "",
		},
	});

	const [updateSeoData] = useUpdateSeoDataMutation();
	const [generateSeoData] = useGetSeoDataMutation();

	const onGenerateSeoData = () => {
		if (!product) return;

		generateSeoData(product.id)
			.unwrap()
			.then((data) => {
				reset({
					title: data.title || "",
					description: data.description || "",
				});
				toast.success(
					intl.formatMessage({
						id: "seoDataHasBeenSuccessfullyGenerated",
					})
				);
			})
			.catch((error) => {
				if (error?.data?.error) {
					toast.error(error.data.error[0]);
				} else {
					toast.error(
						intl.formatMessage({ id: "somethingWentWrong" })
					);
				}
			});
	};

	const onSubmit: SubmitHandler<{
		title: string;
		description: string;
	}> = (data) => {
		if (!product) return;

		updateSeoData({
			id: product.id,
			title: data.title,
			description: data.description,
		})
			.unwrap()
			.then(() => {
				onHide();
				toast.success(
					intl.formatMessage({ id: "seoDataSuccessfullyUpdated" })
				);
			})
			.catch((error) => {
				if (error?.data?.error) {
					toast.error(error.data.error[0]);
				} else {
					toast.error(
						intl.formatMessage({ id: "somethingWentWrong" })
					);
				}
			});
	};

	useEffect(() => {
		if (product) {
			reset({
				title: product.seo_data?.title || "",
				description: product.seo_data?.description || "",
			});
		}
	}, [product, reset]);

	return (
		<Modal className="modal-500" centered show={show} onHide={onHide}>
			<Modal.Header className="modal__header" closeButton>
				<Modal.Title className="modal__title">
					<FormattedMessage id="seoManage" />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="pt-0">
				<Form onSubmit={handleSubmit(onSubmit)}>
					<Form.Group>
						<Form.Label>
							<FormattedMessage id="title" />
						</Form.Label>
						<Form.Control
							type="text"
							className={errors.title ? "input-error" : undefined}
							placeholder={intl.formatMessage({
								id: "seoTitlePlaceholder",
							})}
							{...register("title", {
								required: {
									value: true,
									message: intl.formatMessage({
										id: "thisFieldIsRequired",
									}),
								},
							})}
						/>
						{errors.title && (
							<p className="mb-0 form-field-error">
								{errors.title.message}
							</p>
						)}
					</Form.Group>
					<Form.Group controlId="seoDescription" className="mt-3">
						<Form.Label>
							<FormattedMessage id="description" />
						</Form.Label>
						<Form.Control
							as="textarea"
							className={
								errors.description ? "input-error" : undefined
							}
							rows={3}
							placeholder={intl.formatMessage({
								id: "seoDescriptionPlaceholder",
							})}
							{...register("description", {
								required: {
									value: true,
									message: intl.formatMessage({
										id: "thisFieldIsRequired",
									}),
								},
							})}
						/>
						{errors.description && (
							<p className="mb-0 form-field-error">
								{errors.description.message}
							</p>
						)}
					</Form.Group>
					<div className="d-flex justify-content-end mt-4 gap-3">
						<button
							type="button"
							className="ice-button w-100"
							onClick={onGenerateSeoData}
						>
							<FormattedMessage id="generateWithAI" />
						</button>

						<button
							data-testid="save-product-updates"
							type="submit"
							className="ice-button w-100"
						>
							<FormattedMessage id="save" />
						</button>
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

export default EditSeo;
